<template>
  <div class="transaction-form">
    <div class="page-header">
      <h1>{{ isEditing ? 'Edit Transaction' : 'Add Transaction' }}</h1>
    </div>

    <!-- Loading State -->
    <div v-if="loading && isEditing" class="loading-state">
      <el-skeleton :rows="8" animated />
    </div>

    <el-card class="form-card" v-else>
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="120px"
        @submit.prevent="handleSubmit"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Type" prop="type">
              <el-radio-group v-model="form.type">
                <el-radio label="sale">Sale</el-radio>
                <el-radio label="cost">Cost</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          
          <el-col :span="12">
            <el-form-item label="Amount" prop="amount">
              <el-input
                v-model.number="form.amount"
                type="number"
                placeholder="0.00"
                :prefix="form.type === 'sale' ? '+' : '-'"
                step="0.01"
                min="0"
                @input="(value) => { console.log('Amount input:', value, 'Type:', typeof value) }"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Description" prop="description">
              <el-input
                v-model="form.description"
                placeholder="Enter description"
                maxlength="500"
                show-word-limit
              />
            </el-form-item>
          </el-col>
          
          <el-col :span="12">
            <el-form-item label="Category" prop="category">
              <el-input
                v-model="form.category"
                placeholder="Enter category"
                maxlength="100"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Date" prop="date">
              <el-date-picker
                v-model="form.date"
                type="datetime"
                placeholder="Select date and time"
                format="YYYY-MM-DD HH:mm"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          
          <el-col :span="12">
            <el-form-item label="Payment Method" prop="paymentMethod">
              <el-select v-model="form.paymentMethod" placeholder="Select payment method">
                <el-option label="Cash" value="cash" />
                <el-option label="Card" value="card" />
                <el-option label="Transfer" value="transfer" />
                <el-option label="Other" value="other" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Notes">
          <el-input
            v-model="form.notes"
            type="textarea"
            :rows="3"
            placeholder="Additional notes (optional)"
            maxlength="1000"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="Receipt">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :file-list="fileList"
            :limit="1"
            accept="image/*,.pdf"
            drag
            class="receipt-upload"
          >
            <div class="upload-icon">📄</div>
            <div class="el-upload__text">
              Drop receipt file here or <em>click to upload</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                Upload receipt image (JPG, PNG) or PDF file. Max size: 5MB
                <br>
                <small>📷 OCR will automatically extract data from images</small>
              </div>
            </template>
          </el-upload>
          
          <!-- OCR Loading State -->
          <div v-if="ocrLoading" class="ocr-loading">
            <el-icon class="is-loading">⏳</el-icon>
            <span>Processing receipt with OCR...</span>
          </div>
          
          <!-- OCR Results -->
          <div v-if="ocrResult && !ocrLoading" class="ocr-results">
            <el-alert
              :title="ocrResult.success ? 'OCR Results' : 'OCR Processing Failed'"
              :type="ocrResult.success ? 'success' : 'error'"
              :description="ocrResult.success ? 'Receipt data extracted successfully' : ocrResult.error"
              show-icon
              :closable="false"
            />
            
            <!-- Raw OCR Text -->
            <div v-if="ocrResult.success && ocrResult.rawText" class="ocr-raw-text">
              <h4>Raw OCR Text:</h4>
              <div class="raw-text-content">
                <pre>{{ ocrResult.rawText }}</pre>
              </div>
            </div>
            
            <div v-if="ocrResult.success && ocrResult.parsedData" class="ocr-data">
              <h4>Parsed Data:</h4>
              <ul>
                <li v-if="ocrResult.parsedData.merchant">
                  <strong>Merchant:</strong> {{ ocrResult.parsedData.merchant }}
                </li>
                <li v-if="ocrResult.parsedData.amount">
                  <strong>Amount:</strong> ${{ ocrResult.parsedData.amount }}
                </li>
                <li v-if="ocrResult.parsedData.date">
                  <strong>Date:</strong> {{ ocrResult.parsedData.date }}
                </li>
                <li v-if="ocrResult.parsedData.items.length > 0">
                  <strong>Items:</strong> {{ ocrResult.parsedData.items.length }} items found
                </li>
              </ul>
            </div>
          </div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleSubmit" :loading="loading">
            {{ isEditing ? 'Update' : 'Create' }} Transaction
          </el-button>
          <el-button @click="$router.back()">Cancel</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import { processReceipt } from '@/utils/ocr'


export default {
  name: 'TransactionForm',
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    
    const formRef = ref(null)
    const uploadRef = ref(null)
    const loading = ref(false)
    const ocrLoading = ref(false)
    const transactionId = route.params.id
    const fileList = ref([])
    const ocrResult = ref(null)
    
    const isEditing = computed(() => !!transactionId)

    const form = reactive({
      type: 'cost',
      amount: '',
      description: '',
      category: 'ซื้อของ',
      date: new Date(),
      paymentMethod: 'cash',
      notes: '',
      tags: [],
      isRecurring: false,
      recurringPeriod: ''
    })

    const validateAmount = (rule, value, callback) => {
      if (!value) {
        callback(new Error('Please enter amount'))
        return
      }
      
      const amount = parseFloat(value)
      if (isNaN(amount)) {
        callback(new Error('Amount must be a valid number'))
        return
      }
      
      if (amount <= 0) {
        callback(new Error('Amount must be greater than 0'))
        return
      }
      
      callback()
    }

    const rules = {
      type: [
        { required: true, message: 'Please select transaction type', trigger: 'change' }
      ],
      amount: [
        { required: true, message: 'Please enter amount', trigger: 'blur' },
        { validator: validateAmount, trigger: 'blur' }
      ],
      description: [
        { required: true, message: 'Please enter description', trigger: 'blur' },
        { min: 3, max: 500, message: 'Description must be between 3 and 500 characters', trigger: 'blur' }
      ],
      category: [
        { required: true, message: 'Please enter category', trigger: 'blur' },
        { min: 2, max: 100, message: 'Category must be between 2 and 100 characters', trigger: 'blur' }
      ],
      date: [
        { required: true, message: 'Please select date', trigger: 'change' }
      ],
      paymentMethod: [
        { required: true, message: 'Please select payment method', trigger: 'change' }
      ]
    }

    const handleFileChange = async (file) => {
      const isValidType = file.raw.type.startsWith('image/') || file.raw.type === 'application/pdf'
      const isLt5M = file.raw.size / 1024 / 1024 < 5

      if (!isValidType) {
        toast.error('Receipt file must be an image or PDF!')
        return false
      }
      if (!isLt5M) {
        toast.error('Receipt file size can not exceed 5MB!')
        return false
      }
      
      fileList.value = [file]
      
      // Process OCR for image files
      if (file.raw.type.startsWith('image/')) {
        try {
          ocrLoading.value = true
          const result = await processReceipt(file.raw)
          ocrResult.value = result
          
          if (result.success && result.parsedData.amount) {
            // Auto-fill form with OCR data
            form.amount = result.parsedData.amount.toString()
            if (result.parsedData.merchant) {
              form.description = result.parsedData.merchant
            }
            if (result.parsedData.date) {
              // Try to parse the date
              const parsedDate = new Date(result.parsedData.date)
              if (!isNaN(parsedDate.getTime())) {
                form.date = parsedDate
              }
            }
            toast.success('Receipt processed successfully! Data auto-filled.')
          } else {
            toast.warning('Receipt uploaded but OCR could not extract data. Please fill manually.')
          }
        } catch (error) {
          toast.error('OCR processing failed. Please fill form manually.')
        } finally {
          ocrLoading.value = false
        }
      }
      
      return true
    }

    const handleFileRemove = () => {
      fileList.value = []
      ocrResult.value = null
    }

    // Load existing transaction data when editing
    const loadTransaction = async () => {
      if (!isEditing.value) return
      
      try {
        console.log('🔄 Loading transaction data for ID:', transactionId)
        loading.value = true
        
        const transaction = await store.dispatch('transactions/getTransaction', transactionId)
        
        if (transaction) {
          console.log('📝 Loaded transaction data:', transaction)
          
          // Populate form with existing data
          form.type = transaction.type
          form.amount = transaction.amount.toString()
          form.description = transaction.description
          form.category = transaction.category
          form.date = new Date(transaction.date)
          form.paymentMethod = transaction.paymentMethod || 'cash'
          form.notes = transaction.notes || ''
          form.tags = transaction.tags ? (Array.isArray(transaction.tags) ? transaction.tags : transaction.tags.split(',')) : []
          form.isRecurring = transaction.isRecurring || false
          form.recurringPeriod = transaction.recurringPeriod || ''
          
          console.log('✅ Form populated with transaction data')
        } else {
          console.error('❌ Transaction not found')
          toast.error('Transaction not found')
          router.push('/transactions')
        }
      } catch (error) {
        console.error('❌ Error loading transaction:', error)
        toast.error('Failed to load transaction data')
        router.push('/transactions')
      } finally {
        loading.value = false
      }
    }

    const handleSubmit = async () => {
      try {
        console.log('🚀 Starting transaction submission...')
        console.log('📝 Form data:', form)
        console.log('💰 Amount type:', typeof form.amount, 'Value:', form.amount)
        
        // No authentication required - user can always create transactions
        console.log('✅ User can create transactions - no authentication required')
        
        // Validate form manually first
        try {
          await formRef.value.validate()
          console.log('✅ Form validation passed')
        } catch (validationError) {
          console.error('❌ Form validation failed:', validationError)
          if (validationError && typeof validationError === 'object') {
            // Handle validation errors
            const errorMessages = []
            for (const field in validationError) {
              if (Array.isArray(validationError[field])) {
                errorMessages.push(...validationError[field])
              } else {
                errorMessages.push(validationError[field])
              }
            }
            toast.error(errorMessages.join(', '))
            return
          }
          throw validationError
        }
        
        loading.value = true
        
        // Ensure amount is valid
        const amount = parseFloat(form.amount)
        console.log('🔢 Parsed amount:', amount, 'Type:', typeof amount)
        
        if (isNaN(amount) || amount <= 0) {
          throw new Error('Invalid amount. Please enter a valid number greater than 0.')
        }
        
        // Ensure required fields are present
        if (!form.description || !form.category) {
          throw new Error('Description and category are required')
        }
        
        const transactionData = {
          type: form.type,
          amount: amount,
          description: form.description,
          category: form.category,
          date: new Date(form.date).toISOString(),
          paymentMethod: form.paymentMethod,
          notes: form.notes || '',
          tags: form.tags || [],
          isRecurring: form.isRecurring || false,
          recurringPeriod: form.recurringPeriod || null
        }
        
        console.log('📊 Transaction data to send:', transactionData)
        
        // Add receipt file if uploaded
        if (fileList.value.length > 0) {
          transactionData.receipt = fileList.value[0].raw
          console.log('📎 Receipt file attached')
        }
        
        if (isEditing.value) {
          console.log('🔄 Updating existing transaction...')
          await store.dispatch('transactions/updateTransaction', {
            id: transactionId,
            data: transactionData
          })
          toast.success('Transaction updated successfully')
        } else {
          console.log('➕ Creating new transaction...')
          await store.dispatch('transactions/createTransaction', transactionData)
          toast.success('Transaction created successfully')
        }
        
        console.log('✅ Transaction saved successfully')
        router.push('/transactions')
      } catch (error) {
        console.error('❌ Transaction save error:', error)
        console.error('❌ Error details:', error.response?.data)
        
        if (error.response?.data?.error) {
          toast.error(error.response.data.error)
        } else if (error.message) {
          toast.error(error.message)
        } else {
          toast.error('Failed to save transaction')
        }
      } finally {
        loading.value = false
      }
    }

    // Load transaction data when component mounts (for editing)
    onMounted(() => {
      if (isEditing.value) {
        loadTransaction()
      }
    })

    return {
      formRef,
      uploadRef,
      form,
      rules,
      loading,
      ocrLoading,
      isEditing,
      fileList,
      ocrResult,
      handleFileChange,
      handleFileRemove,
      handleSubmit
    }
  }
}
</script>

<style scoped>
.transaction-form {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.loading-state {
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.page-header h1 {
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
  color: #2c3e50;
}

.form-card {
  border-radius: 8px;
  max-width: 800px;
  margin: 0 auto;
}

.el-form-item {
  margin-bottom: 20px;
}

@media (max-width: 768px) {
  .el-col {
    width: 100% !important;
  }
}

.receipt-upload {
  width: 100%;
}

.receipt-upload .el-upload-dragger {
  width: 100%;
  height: 120px;
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s;
}

.receipt-upload .el-upload-dragger:hover {
  border-color: #409eff;
}

.receipt-upload .upload-icon {
  font-size: 28px;
  color: #8c939d;
  margin-bottom: 8px;
  text-align: center;
}

.receipt-upload .el-upload__text {
  color: #606266;
  font-size: 14px;
  text-align: center;
}

.receipt-upload .el-upload__tip {
  font-size: 12px;
  color: #606266;
  margin-top: 8px;
}

.ocr-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
  background-color: #f0f9ff;
  border-radius: 6px;
  color: #409eff;
}

.ocr-results {
  margin-top: 12px;
}

.ocr-data {
  margin-top: 12px;
  padding: 12px;
  background-color: #f6ffed;
  border-radius: 6px;
  border: 1px solid #b7eb8f;
}

.ocr-data h4 {
  margin: 0 0 8px 0;
  color: #52c41a;
  font-size: 14px;
}

.ocr-data ul {
  margin: 0;
  padding-left: 16px;
}

.ocr-data li {
  margin-bottom: 4px;
  font-size: 13px;
}

.ocr-raw-text {
  margin-top: 12px;
  padding: 12px;
  background-color: #fafafa;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
}

.ocr-raw-text h4 {
  margin: 0 0 8px 0;
  color: #666;
  font-size: 14px;
}

.raw-text-content {
  max-height: 200px;
  overflow-y: auto;
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px;
}

.raw-text-content pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #333;
}
</style> 